import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Sheet } from "lucide-react";
import { fmtDateTime } from "@/lib/date";
import { AdminSync } from "@/pages/Vetting";
import SkillPage from "@/components/network/SkillPage";
import ToolsIndex from "@/components/network/ToolsIndex";

function GroupedNav({ data, current }) {
  const navigate = useNavigate();
  const byGroup = data.groups.map((g) => ({ ...g, items: data.stages.filter((s) => s.group === g.id) }));
  return (
    <>
      <div className="lg:hidden">
        <label className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold" htmlFor="net-skill-select">Choose a skill</label>
        <Select value={current} onValueChange={(v) => navigate(`/network/${v}`)}>
          <SelectTrigger id="net-skill-select" className="mt-1 h-11 bg-white" data-testid="net-skill-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {byGroup.map((g) => (
              <SelectGroup key={g.id}>
                <SelectLabel className="text-[10px] uppercase tracking-[0.2em] text-[#8A6D1F]">{g.label}</SelectLabel>
                {g.items.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`net-select-${s.id}`}>{s.nav}</SelectItem>)}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>
      <nav className="hidden lg:block sticky top-24 self-start space-y-4" aria-label="Skills" data-testid="net-skill-nav">
        {byGroup.map((g) => (
          <div key={g.id}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A6D1F] font-semibold px-3 mb-1">{g.label}</div>
            <ul className="space-y-0.5">
              {g.items.map((s) => (
                <li key={s.id}>
                  <Link to={`/network/${s.id}`} data-testid={`net-nav-${s.id}`} aria-current={s.id === current ? "page" : undefined}
                        className={`block rounded-xl px-3 py-2 text-sm transition-colors ${s.id === current ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-white"}`}>
                    {s.nav}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );
}

export default function Network() {
  const { stageId } = useParams();
  const { user, isViewAs } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/training/network").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the guide"));
  useEffect(() => { load(); }, []);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [stageId]);
  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading guide…</div>;

  const idx = Math.max(0, data.stages.findIndex((s) => s.id === stageId));
  const skill = data.stages[idx];
  const prev = data.stages[idx - 1];
  const next = data.stages[idx + 1];
  const navById = Object.fromEntries(data.stages.map((s) => [s.id, s.nav]));
  return (
    <div className="space-y-6" data-testid="network-page">
      <section className="rounded-2xl bg-[#0A1F44] text-white p-5 sm:p-7 ring-1 ring-[#C9A961]/30">
        <Link to="/hub" className="inline-flex items-center min-h-[44px] text-sm text-white/80 hover:text-white">← Resources</Link>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1">Building Your Network</h1>
        <p className="text-white/70 text-sm mt-1 max-w-2xl">Conversation skills, examples and practice for connecting with people and leading great conversations.</p>
        {user?.role === "admin" && !isViewAs && <a href={data.source_sheet} target="_blank" rel="noopener noreferrer" data-testid="net-open-sheet"
           className="inline-flex items-center gap-1 text-[11px] text-[#C9A961] hover:text-white mt-3"><Sheet className="w-3.5 h-3.5" /> Open Google Sheet (admin)</a>}
      </section>
      <div className="grid gap-6 lg:grid-cols-[250px_1fr]">
        <GroupedNav data={data} current={skill.id} />
        <article className="space-y-6 min-w-0" data-testid={`net-skill-${skill.id}`}>
          <header>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">{skill.title}</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0A1F44] mt-1" data-testid="net-skill-subtitle">{skill.subtitle}</h2>
          </header>
          {skill.id === "tools" ? <ToolsIndex skill={skill} navById={navById} /> : <SkillPage skill={skill} />}
          <div className="flex items-center justify-between gap-3 pt-4">
            {prev ? <Button variant="outline" asChild className="rounded-full h-auto min-h-10 py-2 whitespace-normal text-left max-w-[48%]"><Link to={`/network/${prev.id}`} data-testid="net-prev"><ChevronLeft className="w-4 h-4 mr-1 shrink-0" />{prev.nav}</Link></Button> : <span />}
            {next && <Button asChild className="rounded-full h-auto min-h-10 py-2 whitespace-normal text-right max-w-[48%] bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90"><Link to={`/network/${next.id}`} data-testid="net-next-btn">{next.nav}<ChevronRight className="w-4 h-4 ml-1 shrink-0" /></Link></Button>}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-600 pt-2" data-testid="net-synced-at">
            Content last synced from the master sheet {fmtDateTime(data.synced_at)} (Melbourne)
            {user?.can_sync_content && !isViewAs && <AdminSync slug="network" onDone={load} />}
          </div>
        </article>
      </div>
    </div>
  );
}
